/**
 * Loading the Training page's per-day slot data.
 *
 * The Training page shows one day at a time and keeps TWO copies of a day's
 * content, called slot A and slot B. A day switch loads the new day into the
 * slot that is not on screen and then slides that slot in, so the day you are
 * leaving stays intact for the whole slide. `DaySlotData` below is one such
 * copy.
 *
 * This module holds the loading sequence itself, moved out of `training.ts`
 * (#4410). It was moved for one reason: the sequence writes into a slot across
 * several awaits, so two loads that overlap can interleave and leave a slot in a
 * state no single load can produce. That is worth a test, and a 1,200-line
 * Angular page cannot be reached by the plain mocha tests this project uses. Out
 * here it can be driven directly.
 *
 * The page passes what these functions need as `TrainingSlotLoadDeps` — a small
 * bundle of already-bound calls rather than the whole `UserDb` /
 * `WorkoutLoaderService`. A test can then supply a handful of fake functions
 * instead of building two large services.
 */

import { DateTime } from 'luxon';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { formatWeekdayLong, formatWeekdayDateMed } from '@cf-shared/date-format';
import { FitnessPlan, planIsBuiltButHasNoExercises, workoutHasNoExercises } from '@cf-shared/data-model/fitness-plan';
import { FitnessState, DeclaredState } from '@cf-shared/data-model/user-state';
import { Log_Workout } from '@cf-shared/data-model/log-workout';
import { Log_Entry, LogRecordType, LogRecordState, getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { LocaleSettings } from '@cf-shared/shared-util';
import { WorkoutLoadMode } from 'models/workout-load-mode';
// type-only, so a unit test importing this module does not pull Angular and the
// exercise-database chain in behind them
import type { DocWatcher } from 'services/database-wrapper';
import type { UiExercise } from 'models/workout-ui-models';
import type { WorkoutLoadingContext } from 'services/workout-loader.service';
import { formatRelativeDay } from 'util/formatting';

/** Which of the two day copies the Training page is talking about. */
export type DaySlotId = 'A' | 'B';

/** Everything the Training page shows for ONE day. */
export interface DaySlotData {
  // Source data - derive other properties from this
  logWorkout?: Log_Workout;
  // Cached exercises (async computed)
  exercises: UiExercise[];
  // UI state that cannot be derived
  workoutMode: WorkoutLoadMode;
  noWorkoutMessage: string;
  showPendingWorkoutWarning: boolean;
  pendingWorkoutDateLabel: string;
  pendingWorkoutDateISO: string;
  nextWorkoutDateLabel: string;
  nextWorkoutDayName: string;
  showWorkoutChangedInfo: boolean;
  // #6268: set when this day's workout points at a program day that lists no
  // exercises, so the page can say so and offer Edit Workouts on that day.
  // `workoutIndex` is the day's position in `plan.workouts`.
  emptyProgramDay?: { workoutIndex: number };
  // whether this day copy is showing its loading shell. It lives here, beside
  // the content it covers, so that a slot is ONE thing the page can look up
  // rather than two fields that have to be kept in step by hand. The functions
  // in this module never touch it: the page owns it, because it is also set and
  // cleared around the page-enter slide, which the loading sequence knows
  // nothing about.
  loading: boolean;
  // #4410: how many loads have started for this slot. Only the newest one may
  // write into it — see DaySlotLoad below.
  loadClaim: number;
}

/** A fresh, empty day copy. Used for both slots when the page is created. */
export function createEmptyDaySlotData(): DaySlotData {
  return {
    exercises: [],
    workoutMode: WorkoutLoadMode.Current,
    noWorkoutMessage: '',
    showPendingWorkoutWarning: false,
    pendingWorkoutDateLabel: '',
    pendingWorkoutDateISO: '',
    nextWorkoutDateLabel: '',
    nextWorkoutDayName: '',
    showWorkoutChangedInfo: false,
    loading: false,
    loadClaim: 0,
  };
}

/**
 * One load of one day slot, and its claim on that slot: the newest load owns
 * the slot, and a load that has been overtaken stops writing. Full story — how
 * two loads end up on one slot, and what the blank day looked like — in
 * `design-docs/reactive-sync.md`, "One owner per day slot on the Training page"
 * (#4410).
 *
 * The slot data and the claim travel together in this one object so that no
 * load step can be written, and no caller can be added, that carries one
 * without the other. Every step of a load asks its own load object
 * `stillOwns(...)` after each wait; there is no way to spell that check that
 * silently mints a fresh claim and steals the slot from whoever owns it.
 */
export class DaySlotLoad {
  private constructor(
    /** The day copy this load writes into. */
    readonly slotData: DaySlotData,
    /** The slot's load counter as it stood when this load took the slot. */
    private readonly claim: number,
  ) {}

  /**
   * Take ownership of a slot for a load that is about to start. Every load
   * begins here — this is the only way to get a `DaySlotLoad`, and taking the
   * slot is what it does, so a caller cannot ask for one by accident.
   */
  static begin(slotData: DaySlotData): DaySlotLoad {
    slotData.loadClaim++;
    return new DaySlotLoad(slotData, slotData.loadClaim);
  }

  /** Whether this load still owns the slot, or a newer one has taken over. */
  stillOwns(step: string): boolean {
    if (this.slotData.loadClaim === this.claim) return true;
    // a normal, expected outcome — the newer load is the one the user is waiting
    // for. Logged rather than reported, so it stays visible in a device console
    // capture without filling telemetry with events nobody needs to act on.
    console.log('[trainingSlotLoad] dropping an overtaken load', { step, claim: this.claim, current: this.slotData.loadClaim });
    return false;
  }
}

/**
 * What a load needs from the rest of the app.
 *
 * These are plain functions, already bound to the real services by the page. It
 * is deliberately not `UserDb` and `WorkoutLoaderService` themselves: naming only
 * the handful of calls that are actually used keeps the test fakes small, and
 * makes it obvious at a glance what a slot load can reach.
 */
export interface TrainingSlotLoadDeps {
  /** "Now" in the user's local time, honouring the debug fake-today setting. */
  getNowLocal(): DateTime;
  /** Unit / number-format settings, passed straight into the exercise pipeline. */
  locale: LocaleSettings;
  /** The page's document watcher, so loaded documents re-trigger a reload when they change. */
  watcher: DocWatcher;
  loadPlanAsync(watcher: DocWatcher): Promise<FitnessPlan>;
  loadFitnessStateAsync(watcher: DocWatcher): Promise<FitnessState>;
  loadDeclaredStateAsync(watcher: DocWatcher): Promise<DeclaredState>;
  getLogEntriesInRangeAsync(
    start: DateTime,
    end: DateTime,
    watcher: DocWatcher,
    options?: { inLogEntriesTimeZone?: boolean },
  ): Promise<Log_Entry[]>;
  getWorkoutModeWithContextAsync(
    logWorkout: Log_Workout,
    nowLocal: DateTime,
    watcher: DocWatcher,
  ): Promise<{ mode: WorkoutLoadMode; currentWorkout?: Log_Workout }>;
  loadWorkoutExercisesAsync(
    logWorkout: Log_Workout,
    context: WorkoutLoadingContext,
    mode: WorkoutLoadMode,
  ): Promise<{ exercises: UiExercise[]; skippedExerciseIds: string[]; loadInterrupted: boolean }>;
  /**
   * Report a condition a person may eventually need to look at. Used for the one
   * state that should be impossible — see the canary in buildDaySlotExercisesAsync.
   */
  reportProblem(problemName: string, context: Record<string, unknown>): void;
  /**
   * Which program day a workout is, for DISPLAY. A future workout has no stored
   * reference yet, so this may be a guess — fine for choosing what to show, never
   * to be stored (design-docs/workout-plan-reference.md).
   */
  getEffectivePlanReference(workout: Log_Workout, plan: FitnessPlan): number;
  /** Record a rare-but-handled condition: the user has been told and given a way out. */
  reportInfo(infoName: string, context: Record<string, unknown>): void;
}

/**
 * The workout whose exercise list still has to be built, or null when there is
 * nothing to build (the day has no workout, or the workout was skipped).
 */
export type SlotPendingBuild = { workout: Log_Workout; mode: WorkoutLoadMode } | null;

/**
 * Load a whole day into a slot: the light metadata first, then the heavy
 * exercise list. Used by every warm-path caller (a reactive reload, a date
 * change, coming back from edit-workouts). The FIRST page entry deliberately
 * does not use this — it calls the two halves separately so the exercise build
 * lands after the page-enter slide instead of during it (#4242).
 */
export async function loadDaySlotAsync(
  slotData: DaySlotData,
  date: DateTime,
  deps: TrainingSlotLoadDeps,
): Promise<void> {
  // one load object for both halves — they are two steps of a single load, so an
  // overtaking load must beat them together, not one at a time
  const load = DaySlotLoad.begin(slotData);
  const pendingBuild = await loadDaySlotMetaAsync(load, date, deps);
  if (pendingBuild) {
    await buildDaySlotExercisesAsync(load, pendingBuild.workout, pendingBuild.mode, deps);
  }
}

/**
 * Load only the LIGHT workout metadata for a slot: the plan, the day's workout
 * entry, its mode and pending/skipped state. Sets `logWorkout` and `workoutMode`,
 * which is all the page's header getters (workout name, Start button) need.
 *
 * It does NOT build the exercise list. That heavy ~100 ms step is
 * `buildDaySlotExercisesAsync`, kept separate so the first page entry can show
 * the header DURING the slide while the exercise build waits until after it
 * (#4242: the exercise pipeline's cache-served awaits resolve as microtasks that
 * never yield, so run mid-slide it froze the animation).
 */
export async function loadDaySlotMetaAsync(
  load: DaySlotLoad,
  date: DateTime,
  deps: TrainingSlotLoadDeps,
): Promise<SlotPendingBuild> {
  const slotData = load.slotData;
  const now = deps.getNowLocal();

  // reset slot state
  slotData.logWorkout = undefined;
  slotData.showPendingWorkoutWarning = false;
  slotData.pendingWorkoutDateLabel = '';
  slotData.pendingWorkoutDateISO = '';
  slotData.nextWorkoutDateLabel = '';
  slotData.nextWorkoutDayName = '';
  slotData.showWorkoutChangedInfo = false;
  slotData.emptyProgramDay = undefined;

  // load plan (cached by userDb). The caller's loadPlanAsync is what stores the
  // plan where the page's header getters can see it, so it must run here and not
  // only in the exercise build below.
  await deps.loadPlanAsync(deps.watcher);
  if (!load.stillOwns('plan')) return null;

  // get workout entries for this day
  const currentDayDate = date.toISODate();
  let entries = await deps.getLogEntriesInRangeAsync(
    date.minus({ days: 1 }).startOf('day'),
    date.plus({ days: 1 }).endOf('day'),
    deps.watcher,
    { inLogEntriesTimeZone: true },
  );
  if (!load.stillOwns('entries')) return null;

  entries = entries.filter(e =>
    getEntryLocalTimeInDesiredTimeZone(e, date).toISODate() === currentDayDate &&
    e.type === LogRecordType.Workout
  );

  if (entries.length === 0) {
    // no workout for this day — find next scheduled workout for the rest-day message
    slotData.exercises = [];
    slotData.noWorkoutMessage = LL.training.noWorkout();
    slotData.workoutMode = date < now.startOf('day') ? WorkoutLoadMode.Past : WorkoutLoadMode.Current;
    await loadNextWorkoutDateForSlotAsync(load, date, now, deps);
    return null;
  }

  const workout = entries[0] as Log_Workout;
  slotData.logWorkout = workout;

  // if skipped, find next workout date for the message
  if (workout.state === LogRecordState.Skipped) {
    await loadNextWorkoutDateForSlotAsync(load, date, now, deps);
  }

  // ask for the mode together with the pending-workout context
  // only show warning if pending workout was actually started (Incomplete), not just scheduled
  const result = await deps.getWorkoutModeWithContextAsync(workout, now, deps.watcher);
  if (!load.stillOwns('mode')) return null;
  slotData.workoutMode = result.mode;

  // if SoftFuture with in-progress workout, set up pending workout warning
  if (result.mode === WorkoutLoadMode.SoftFuture && result.currentWorkout && result.currentWorkout.state === LogRecordState.Incomplete) {
    const todayDay = now.startOf('day');
    const currentWorkoutDay = getEntryLocalTimeInDesiredTimeZone(result.currentWorkout, now).startOf('day');
    slotData.pendingWorkoutDateLabel = formatRelativeDay(todayDay, currentWorkoutDay);
    slotData.pendingWorkoutDateISO = currentWorkoutDay.toISODate();
    slotData.showPendingWorkoutWarning = true;
  }

  // nothing to build for a skipped workout
  if (workout.state === LogRecordState.Skipped) {
    slotData.exercises = [];
    return null;
  }

  return { workout, mode: slotData.workoutMode };
}

/**
 * Build the heavy exercise list for a slot (the ~100 ms exercise pipeline).
 * Split out of the metadata load so the first page entry can run it AFTER the
 * page-enter slide (#4242).
 */
export async function buildDaySlotExercisesAsync(
  load: DaySlotLoad,
  workout: Log_Workout,
  mode: WorkoutLoadMode,
  deps: TrainingSlotLoadDeps,
): Promise<void> {
  const slotData = load.slotData;
  const [plan, fitnessState, declaredState] = await Promise.all([
    deps.loadPlanAsync(deps.watcher),
    deps.loadFitnessStateAsync(deps.watcher),
    deps.loadDeclaredStateAsync(deps.watcher),
  ]);
  if (!load.stillOwns('context')) return;

  const context: WorkoutLoadingContext = {
    plan,
    fitnessState,
    declaredState,
    nowLocal: deps.getNowLocal(),
    locale: deps.locale,
    watcher: deps.watcher,
    currentWorkoutId: workout._id,
  };

  const { exercises, skippedExerciseIds, loadInterrupted } = await deps.loadWorkoutExercisesAsync(workout, context, mode);
  if (!load.stillOwns('exercises')) return;
  slotData.exercises = exercises;

  // canary for a data-level blank training day (#4410) — an empty list with no
  // failure (skipped ids) and no interruption is the one state nothing else
  // reports. Full reasoning: design-docs/reactive-sync.md, "One owner per day
  // slot on the Training page".
  if (exercises.length === 0 && skippedExerciseIds.length === 0 && !loadInterrupted) {
    // if the whole program has no exercises, #4587 has already reported it and asks
    // the user to rebuild — another report here adds nothing
    if (planIsBuiltButHasNoExercises(plan)) return;

    // if the program day this workout points at lists no exercises (#6268), the
    // page explains it and offers Edit Workouts, so it is no longer unexplained
    // a future workout has no stored reference yet, so this asks the same question the
    // page header asks to show the workout's name
    const workoutIndex = deps.getEffectivePlanReference(workout, plan);
    const planDay = plan?.workouts?.[workoutIndex];
    if (planDay && workoutHasNoExercises(planDay)) {
      slotData.emptyProgramDay = { workoutIndex };
      deps.reportInfo('training.day-loaded-from-empty-program-day', {
        workoutId: workout._id,
        workoutIndex,
        workoutCount: plan.workouts.length,
      });
      return;
    }

    // if the workout points at a day the program no longer has (#6850), name that case: the
    // loader repairs an untrained workout the user can train now, so what is left here is a
    // started workout or one further ahead or behind
    if (plan?.workouts?.length && !planDay) {
      deps.reportProblem('training.day-loaded-with-out-of-range-plan-reference', {
        workoutId: workout._id,
        workoutState: workout.state,
        mode,
        workoutIndex,
        workoutCount: plan.workouts.length,
      });
      return;
    }

    deps.reportProblem('training.day-loaded-with-no-exercises', {
      workoutId: workout._id,
      workoutState: workout.state,
      mode,
      // separates a day whose exercises were dropped from a day that never had
      // any: "workout wanted N, built 0" means the loader could not build them,
      // while "wanted 0, built 0" means the program's workout is itself empty.
      // Since #4614 the first case is much rarer — an exercise whose plan
      // subslot is gone is now drawn from the workout log's own snapshot, and
      // is dropped only when the log has no snapshot either (a session logged
      // before that snapshot existed).
      workoutExerciseCount: workout.exercises?.length ?? 0,
    });
  }
}

/** Look 7 days ahead for the next non-skipped workout, for the rest-day message. */
async function loadNextWorkoutDateForSlotAsync(
  load: DaySlotLoad,
  date: DateTime,
  now: DateTime,
  deps: TrainingSlotLoadDeps,
): Promise<void> {
  const slotData = load.slotData;
  const futureEntries = await deps.getLogEntriesInRangeAsync(
    date.plus({ days: 1 }).startOf('day'),
    date.plus({ days: 7 }).endOf('day'),
    deps.watcher,
    { inLogEntriesTimeZone: true },
  );
  if (!load.stillOwns('next-workout')) return;

  const nextWorkout = futureEntries.find(entry =>
    entry.type === LogRecordType.Workout && entry.state !== LogRecordState.Skipped
  ) as Log_Workout;

  if (nextWorkout) {
    const nextWorkoutDate = getEntryLocalTimeInDesiredTimeZone(nextWorkout, now);
    slotData.nextWorkoutDateLabel = formatWeekdayDateMed(nextWorkoutDate, BOOT_LOCALE);
    slotData.nextWorkoutDayName = formatWeekdayLong(nextWorkoutDate, BOOT_LOCALE);
  }
}
